import { useState } from 'react';
import log from 'loglevel';
import { useTranslation } from 'react-i18next';
import { History, Unarchive } from '@mui/icons-material';
import STRING_KEYS from '../../../language/keys';
import { ViewList } from '../../../icons/view-list';
import { ActionDelete } from '../../../icons/action-delete';
import useNavigate from '../../../hooks/useNavigate';
import TableAction from '../../../components/MenuMoreVert';
import { TABLE } from '../../../consts';
import { ConfirmationDialogType, POSITION_STATE, VIEW_MODE } from '../../../consts/enums';
import ConfirmationDialog from '../../../components/Dialogs/ConfirmationDialog';
import { PERMISSIONS, PERMISSIONS_GROUPS } from '../../../consts/permissions';
import { useAuth } from '../../../hooks/useAuth';
import { PositionCloseDialog } from '../../../components/PositionDetails/PositionCloseDialog';
import { PositionReopenDialog } from '../../../components/PositionDetails/PositionReopenDialog';
import { Archive } from '../../../icons/archive';
import { PositionState } from '../../../@types/position';

const TAG = '[POSITION_ACTION_COMPONENT]';
const { ACTIONS } = TABLE;

type PositionActionProps = {
  positionId: string,
  accountId: string,
  positionState: string,
  onDeleteHandler: (positionId: string, accountId: string) => void
  onClosePositionHandler: (positionId: string, accountId: string, positionState: PositionState) => void
}

const PositionAction = ({
  positionId, accountId, onDeleteHandler, onClosePositionHandler, positionState,
}: PositionActionProps): JSX.Element | null => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isPositionCloseDialogVisible, setPositionCloseDialogVisibility] = useState(false);
  const [isPositionReopenDialogVisible, setPositionReopenDialogVisibility] = useState(false);

  const {
    goToPositionWithModalProp,
  } = useNavigate();
  const { t: translate } = useTranslation();
  const { hasPermissions } = useAuth();

  const handlePositionCloseClick = () => {
    setPositionCloseDialogVisibility(true);
  };
  const handleHidePositionCloseDialog = () => {
    setPositionCloseDialogVisibility(false);
  };

  const handlePositionReopenClick = () => {
    setPositionReopenDialogVisibility(true);
  };
  const handleHidePositionReopenDialog = () => {
    setPositionReopenDialogVisibility(false);
  };

  const isPositionClosed = positionState === POSITION_STATE.CLOSED;

  const getDefaultActions = () => {
    const actions = [
      {
        type: ACTIONS.VIEW,
        title: STRING_KEYS.VIEW_MATCHES,
        icon: <ViewList />,
        permissions: PERMISSIONS_GROUPS.POSITION_MATCHES_LIST_PAGE,
      },
      {
        type: ACTIONS.VIEW_HISTORY_LOG,
        title: STRING_KEYS.POSITIONS_DETAILS_TABLE_VIEW_ACTIVITY_ACTION,
        icon: <History />,
        permissions: [PERMISSIONS.POSITION_HISTORY_LOG_VIEW],
      },
      {
        type: ACTIONS.DELETE,
        title: STRING_KEYS.DELETE,
        icon: <ActionDelete />,
        permissions: [PERMISSIONS.POSITION_DELETE],
      },
    ];

    actions.push(isPositionClosed ? {
      type: ACTIONS.REOPEN,
      title: STRING_KEYS.REOPEN,
      icon: <Unarchive />,
      permissions: [PERMISSIONS.POSITION_EDIT],
    } : {
      type: ACTIONS.CHANGE_STATUS,
      title: STRING_KEYS.CLOSE,
      icon: <Archive />,
      permissions: [PERMISSIONS.POSITION_EDIT],
    });

    return actions;
  };

  const actions = getDefaultActions().filter((action) => {
    return !action.permissions || hasPermissions(action.permissions);
  }).map((defaultAction) => {
    const title = translate(defaultAction.title);

    return {
      ...defaultAction,
      key: positionId,
      title,
    };
  });
  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleConfirmDeleteDialog = () => {
    handleCloseDeleteDialog();
    onDeleteHandler(positionId, accountId);
  };

  const handleActions = (key: string, type: string) => {
    switch (type) {
      case ACTIONS.CHANGE_STATUS:
        handlePositionCloseClick();
        break;
      case ACTIONS.REOPEN:
        handlePositionReopenClick();
        break;
      case ACTIONS.DELETE:
        handleOpenDeleteDialog();
        break;
      case ACTIONS.VIEW_HISTORY_LOG:
        goToPositionWithModalProp(positionId);
        break;
      default:
        log.warn(TAG, `Cannot find any handler for type:${type}`);
    }
  };

  if (!actions || actions.length <= 0) {
    return null;
  }

  return (
    <>
      <TableAction actions={ actions } handleActions={ handleActions } />
      <ConfirmationDialog
        isOpen={ openDeleteDialog }
        title={ translate(STRING_KEYS.POSITION_DELETE_DIALOG.TITLE) }
        subTitle={ translate(STRING_KEYS.POSITION_DELETE_DIALOG.SUB_TITLE) }
        btnTitle={ translate(STRING_KEYS.POSITION_DELETE_DIALOG.ACTION_BTN) }
        onConfirm={ handleConfirmDeleteDialog }
        onClose={ handleCloseDeleteDialog }
        type={ ConfirmationDialogType.error }
      />
      <PositionCloseDialog
        onCloseDialog={ handleHidePositionCloseDialog }
        isOpen={ isPositionCloseDialogVisible }
        viewMode={ VIEW_MODE.EDITABLE }
        onPositionStateChange={ (values) => {
          handleHidePositionCloseDialog();
          onClosePositionHandler(positionId, accountId, values);
        } }
      />
      <PositionReopenDialog
        onCloseDialog={ handleHidePositionReopenDialog }
        isOpen={ isPositionReopenDialogVisible }
        onPositionStateChange={ (values) => {
          handleHidePositionReopenDialog();
          onClosePositionHandler(positionId, accountId, values);
        } }
      />
    </>
  );
};

export default PositionAction;
